import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";
import { isTopLayer, popLayer, pushLayer } from "../ui/layers";
import { TaskDetail } from "./TaskDetail";

/** Side panel that shows a task over the current page, driven by the ?task= search param. */
export function TaskPeek({ taskId }: { taskId: string }) {
  const [params, setParams] = useSearchParams();
  const [layer, setLayer] = useState(0);

  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("task");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const id = pushLayer();
    setLayer(id);
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = target.closest("input, textarea, [contenteditable=true]");
      if (e.key === "Escape" && isTopLayer(id) && !typing) close();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      popLayer(id);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return createPortal(
    <div data-layer={layer} className="fixed inset-0 z-30 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onMouseDown={close} />
      <aside className="relative h-full w-full max-w-[820px] overflow-y-auto border-s border-line bg-canvas shadow-pop scroll-thin">
        <div className="px-6 pt-4 pb-24 sm:px-12">
          <TaskDetail key={taskId} taskId={taskId} mode="peek" onClose={close} />
        </div>
      </aside>
    </div>,
    document.body,
  );
}

export function useOpenTask() {
  const [params, setParams] = useSearchParams();
  return (taskId: string) => {
    const next = new URLSearchParams(params);
    next.set("task", taskId);
    setParams(next);
  };
}
